import { useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { LogOut, Menu, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { Sidebar } from './Sidebar'

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  medico: 'Médico',
  tens: 'TENS',
  digitador: 'Digitador',
  recepcionista: 'Recepcionista',
}

export default function AppLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  if (!user) return null // ProtectedRoute cubre este caso antes de llegar acá

  return (
    <div className="flex min-h-dvh bg-slate-50">
      <Sidebar role={user.role} open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col md:ml-0">
        <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 md:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded p-1.5 text-slate-600 hover:bg-slate-100 md:hidden"
            aria-label="Abrir menú"
            data-testid="open-sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="hidden flex-col text-right sm:flex">
              <span className="text-sm font-medium text-slate-900" data-testid="user-email">
                {user.email}
              </span>
              <span className="text-xs text-slate-500" data-testid="user-role">
                {ROLE_LABEL[user.role] ?? user.role}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout()
                navigate('/login', { replace: true })
              }}
              data-testid="logout-button"
              leftIcon={<LogOut className="h-4 w-4" />}
            >
              Salir
            </Button>
          </div>
        </header>
        {/* F3 banner MFA — admin SIN MFA configurado (user.mfaEnabled=false) */}
        {user.role === 'admin' && !user.mfaEnabled && (
          <div
            className="flex flex-col gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 md:flex-row md:items-center md:justify-between md:px-6"
            data-testid="mfa-setup-banner"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                Configura la autenticación de dos pasos (MFA) para proteger tu cuenta de
                administrador.
              </p>
            </div>
            <Link
              to="/profile/security"
              className="inline-flex h-9 items-center justify-center rounded-md border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-900 hover:bg-amber-100"
              data-testid="mfa-setup-banner-cta"
            >
              Configurar MFA
            </Link>
          </div>
        )}
        <main className="flex-1 overflow-x-hidden px-4 py-6 md:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
